import { Effect } from 'effect';
import { attemptStudio, attemptStudioPromise, runStudioPromise } from '../effect-runtime';
import { withStudioPdfDocumentEffect, type PageText } from './pdf-text';

/**
 * One page's text layer as plain text, joined the way Extract text joins it:
 * a line break where PDF.js marks the end of a line, a space otherwise.
 */
export function studioPagePlainText(content: PageText): string {
  let text = '';
  for (const item of content.items) {
    if (!('str' in item)) continue;
    text += item.str;
    text += item.hasEOL ? '\n' : ' ';
  }
  return text.trim();
}

/**
 * The text of each page, in page order; a page with no text layer (a scan) is
 * an empty string. `maxPages` stops the read early, so a host that only keeps
 * the first pages does not walk a thousand-page file to throw the rest away.
 */
export const extractStudioPageTextsEffect = Effect.fn('studio.pageTexts.extract')(function* (
  bytes: Uint8Array,
  maxPages?: number,
) {
  return yield* withStudioPdfDocumentEffect(bytes, (pdf) =>
    Effect.gen(function* () {
      const count = yield* attemptStudio('pdfjs', 'page-texts-count', () => pdf.numPages);
      const last = maxPages === undefined ? count : Math.min(count, Math.max(0, maxPages));
      const pages: string[] = [];
      for (let pageNumber = 1; pageNumber <= last; pageNumber += 1) {
        const page = yield* attemptStudioPromise('pdfjs', 'page-texts-page', () =>
          pdf.getPage(pageNumber),
        );
        const content = yield* attemptStudioPromise('pdfjs', 'page-texts-content', () =>
          page.getTextContent(),
        );
        pages.push(yield* attemptStudio('pdfjs', 'page-texts-join', () => studioPagePlainText(content)));
      }
      return pages;
    }),
  );
});

/** Promise form for hosts. Aborting `signal` stops the read and closes the document. */
export function extractStudioPageTexts(
  bytes: Uint8Array,
  options?: { maxPages?: number; signal?: AbortSignal },
): Promise<string[]> {
  return runStudioPromise(extractStudioPageTextsEffect(bytes, options?.maxPages), {
    signal: options?.signal,
  });
}
